/**
 * Contrato Pro de paragens/destinos (módulo puro, isolado do Premium).
 *
 * Interpreta pedidos do tipo "2 dias a Atenas, 5 dias a Creta e 2 em Santorini"
 * e devolve um contrato vinculativo com a ordem, os dias por destino e o total
 * de dias — sempre pela SOMA das paragens, nunca pelo primeiro número.
 *
 * Nenhuma função deste ficheiro é usada pelo fluxo Premium.
 */

import { isFunctionalRequestWord, splitRequiredStopsClause } from "./required-stops";

export type MultiDestinationStop = {
  /** Nome mostrado ao utilizador (preservado tal como escrito). */
  destination: string;
  /** Chave normalizada para comparações internas. */
  key: string;
  days: number;
  order: number;
};

export type MultiDestinationPlan = {
  tripType: "multi_destination";
  stops: MultiDestinationStop[];
  totalDays: number;
};

/** Validação estrutural gratuita antes de qualquer reserva de quota. */
export function isValidMultiDestinationPlan(
  plan: MultiDestinationPlan | null | undefined,
): plan is MultiDestinationPlan {
  if (!plan || plan.tripType !== "multi_destination" || plan.stops.length < 2) return false;
  if (plan.stops.some((stop) => !isUsableDestinationName(stop.destination) || stop.days < 1)) {
    return false;
  }
  const total = plan.stops.reduce((sum, stop) => sum + stop.days, 0);
  return total === plan.totalDays && total >= 2 && total <= 60;
}

const STOP_WORDS = new Set([
  "o", "a", "os", "as", "um", "uma", "que", "ver", "viagem", "viajar", "ir", "casa",
  "dias", "dia", "horas", "hora", "noites", "noite", "la", "ali", "cada", "total",
  "days", "day", "nights", "night", "there", "here", "trip", "the", "and", "with",
  "semana", "semanas", "week", "weeks", "mes", "meses", "pessoas", "pessoa",
]);

const NUMBER_WORDS: Record<string, number> = {
  um: 1, uma: 1, dois: 2, duas: 2, tres: 3, quatro: 4, cinco: 5, seis: 6, sete: 7,
  oito: 8, nove: 9, dez: 10,
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
};

export function normalizeDestinationKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .replace(/\s+/gu, " ");
}

/** Um nome de destino nunca pode ser a frase do utilizador. */
export function isUsableDestinationName(value: string): boolean {
  const clean = value.trim();
  if (clean.length < 2 || clean.length > 40) return false;
  if (/\d/u.test(clean)) return false;
  if (clean.split(/\s+/u).length > 4) return false;
  if (/\b(dias?|days?|noites?|nights?|horas?|hours?)\b/iu.test(clean)) return false;
  // Palavras funcionais do pedido ("uma rota em…") nunca são destinos.
  if (isFunctionalRequestWord(clean)) return false;
  return !STOP_WORDS.has(normalizeDestinationKey(clean));
}

/**
 * Sanitiza um nome de base: devolve `null` quando o valor é claramente a frase
 * original do pedido em vez de um destino.
 */
export function sanitizeDestinationName(value: string): string | null {
  const clean = value.replace(/[\s,;.!?]+$/u, "").replace(/^[\s,;.!?]+/u, "").trim();
  return isUsableDestinationName(clean) ? clean : null;
}

const DURATION_UNIT = "(?:dias?|days?|d[ií]as?|giorni?|jours?|tage?|noites?|nights?|notti?|n[aä]chte)";
const PREPOSITION = "(?:em|no|na|nas|nos|a|ao|aos|à|às|para|in|to|at|en|dans|nel|nella|auf|bei)";

const SEGMENT_RE = new RegExp(
  `(?<![\\p{L}])(\\d{1,2}|\\p{L}{2,6})\\s*${DURATION_UNIT}\\s*${PREPOSITION}(?![\\p{L}])\\s+`,
  "giu",
);

/** Padrão elíptico: "e 2 em Santorini" (unidade subentendida pela lista). */
const ELLIPTIC_SEGMENT_RE = new RegExp(
  `(?<![\\p{L}\\p{N}])(\\d{1,2})\\s+${PREPOSITION}(?![\\p{L}])\\s+`,
  "giu",
);

/** Padrão invertido: "a Atenas 2 dias" / "in Rome 3 days". */
const REVERSED_SEGMENT_RE = new RegExp(
  `(?<![\\p{L}])(${PREPOSITION})\\s+(?:\\p{Lu}[\\p{L}'’-]*\\s+){1,3}(\\d{1,2}|\\p{L}{2,6})\\s*${DURATION_UNIT}`,
  "giu",
);

const CONNECTORS = new Set(["de", "do", "da", "dos", "das", "del", "la", "le", "of", "von", "d'"]);

function parseCount(raw: string): number | null {
  if (/^\d+$/u.test(raw)) {
    const n = Number(raw);
    return n >= 1 && n <= 60 ? n : null;
  }
  const word = NUMBER_WORDS[normalizeDestinationKey(raw)];
  return word ?? null;
}

/** Extrai o nome do destino imediatamente a seguir a uma preposição. */
function readDestination(rest: string): string | null {
  const segment = rest.split(/[,;.!?\n]|\s+(?:e|and|y|et|und)\s+/iu)[0] ?? "";
  const tokens = segment.trim().split(/\s+/u).slice(0, 4);
  const picked: string[] = [];
  for (const token of tokens) {
    const clean = token.replace(/[^\p{L}'’-]/gu, "");
    if (!clean) break;
    const isConnector = CONNECTORS.has(normalizeDestinationKey(clean));
    const startsUpper = /^\p{Lu}/u.test(clean);
    if (picked.length === 0) {
      if (!startsUpper && STOP_WORDS.has(normalizeDestinationKey(clean))) return null;
      picked.push(clean);
      continue;
    }
    if (isConnector || startsUpper) {
      picked.push(clean);
      continue;
    }
    break;
  }
  while (picked.length > 0 && CONNECTORS.has(normalizeDestinationKey(picked[picked.length - 1]))) {
    picked.pop();
  }
  return picked.length ? picked.join(" ") : null;
}

const SEGMENT_SPLIT_RE = /[,;.!?\n]+|\s+(?:e|and|y|et|und)\s+/giu;

/** Prefixos irrelevantes antes do nome da base ("vou", "depois", "em"…). */
const LEADING_NOISE = new Set([
  "vou", "vamos", "quero", "queria", "depois", "ficar", "ficamos", "passar", "passo",
  "estar", "visitar", "conhecer", "seguir", "then", "next", "spend", "stay", "visit",
  "em", "no", "na", "nas", "nos", "a", "ao", "aos", "as", "à", "às", "para", "in",
  "to", "at", "en", "dans", "nel", "nella", "auf", "bei", "mais", "more", "com",
]);

function splitSegments(text: string): Array<{ text: string; at: number }> {
  const out: Array<{ text: string; at: number }> = [];
  let last = 0;
  for (const match of text.matchAll(SEGMENT_SPLIT_RE)) {
    const at = match.index ?? 0;
    out.push({ text: text.slice(last, at), at: last });
    last = at + match[0].length;
  }
  out.push({ text: text.slice(last), at: last });
  return out.filter((segment) => segment.text.trim().length > 0);
}

const SEGMENT_DURATION_RE = new RegExp(
  `(?<![\\p{L}])(\\d{1,2}|\\p{L}{2,6})\\s*${DURATION_UNIT}(?![\\p{L}])`,
  "iu",
);

/**
 * "Creta 5 dias", "5 dias Creta", "Atenas 2 dias" — sem depender da ordem
 * das palavras nem de preposição.
 */
function parseSegmentPair(segment: string): { destination: string; days: number } | null {
  const match = segment.match(SEGMENT_DURATION_RE);
  if (!match || match.index === undefined) return null;
  const days = parseCount(match[1] ?? "");
  if (!days) return null;
  const rest = `${segment.slice(0, match.index)} ${segment.slice(match.index + match[0].length)}`
    .replace(/\s+/gu, " ")
    .trim();
  // Quando existe preposição, o destino é sempre o que vem a seguir
  // ("Tenho 1 dia em Madrid" → Madrid, nunca "Tenho").
  const afterPreposition = rest.match(new RegExp(`(?:^|\\s)${PREPOSITION}\\s+(.+)$`, "iu"));
  const candidate = afterPreposition?.[1] ?? rest;
  const tokens = candidate.split(/\s+/u);
  while (tokens.length > 0 && LEADING_NOISE.has(normalizeDestinationKey(tokens[0].replace(/[^\p{L}'’-]/gu, "")))) {
    tokens.shift();
  }
  const raw = readDestination(tokens.join(" "));

  const destination = raw ? sanitizeDestinationName(raw) : null;
  return destination ? { destination, days } : null;
}

/**
 * Devolve o contrato multi-destino quando o texto nomeia claramente 2+ bases
 * com dias próprios. Caso contrário devolve `null` (nada muda no fluxo atual).
 */
export function parseMultiDestinationPlan(rawText: string): MultiDestinationPlan | null {
  if (!rawText?.trim()) return null;
  // Os locais obrigatórios ("que inclua X, Y, Z") nunca podem virar destinos.
  const text = splitRequiredStopsClause(rawText).before || rawText;
  if (!text.trim()) return null;
  const found: Array<{ destination: string; days: number; at: number }> = [];
  const push = (destination: string, days: number, at: number) => {
    const key = normalizeDestinationKey(destination);
    const existing = found.find((f) => normalizeDestinationKey(f.destination) === key);
    if (existing) {
      existing.days = Math.max(existing.days, days);
      return;
    }
    found.push({ destination, days, at });
  };

  // Padrão A: "2 dias em Atenas"
  const consumedCounts = new Set<number>();
  for (const match of text.matchAll(SEGMENT_RE)) {
    const days = parseCount(match[1] ?? "");
    const at = match.index ?? 0;
    const rest = text.slice(at + match[0].length);
    const raw = readDestination(rest);
    const name = raw ? sanitizeDestinationName(raw) : null;
    if (!days || !name) continue;
    consumedCounts.add(at);
    push(name, days, at);
  }

  // Padrão B: "a Atenas 2 dias" / "in Rome 3 days"
  for (const match of text.matchAll(REVERSED_SEGMENT_RE)) {
    const at = match.index ?? 0;
    const raw = readDestination(text.slice(at + (match[1]?.length ?? 0) + 1));
    const name = raw ? sanitizeDestinationName(raw) : null;
    const days = parseCount(match[2] ?? "");
    if (!days || !name) continue;
    // "Vou 2 dias a Atenas 5 dias a Creta": o "5" já pertence a Creta (padrão A);
    // nunca pode ser reinterpretado como duração de Atenas.
    const countOffset = at + match[0].lastIndexOf(match[2] ?? "");
    if (consumedCounts.has(countOffset)) continue;
    push(name, days, at);
  }


  // Padrão C (independente da ordem): cada segmento separado por vírgula,
  // ponto, ponto-e-vírgula, quebra de linha ou "e" pode conter
  // "Creta 5 dias", "5 dias Creta", "Atenas 2 dias" (sem preposição).
  for (const segment of splitSegments(text)) {
    const parsed = parseSegmentPair(segment.text);
    if (parsed) push(parsed.destination, parsed.days, segment.at);
  }

  // Padrão D (elíptico): "… e 2 em Santorini" — a unidade fica subentendida
  // pela enumeração anterior. Só é aceite quando já existe pelo menos uma
  // paragem com unidade temporal explícita e o número está imediatamente
  // antes da preposição (nunca dentro de um nome próprio).
  if (found.length > 0) {
    for (const match of text.matchAll(ELLIPTIC_SEGMENT_RE)) {
      const at = match.index ?? 0;
      const days = parseCount(match[1] ?? "");
      const raw = readDestination(text.slice(at + match[0].length));
      const name = raw ? sanitizeDestinationName(raw) : null;
      if (!days || !name) continue;
      push(name, days, at);
    }
  }


  found.sort((a, b) => a.at - b.at);
  if (found.length < 2) return null;


  const stops: MultiDestinationStop[] = found.map((f, index) => ({
    destination: f.destination,
    key: normalizeDestinationKey(f.destination),
    days: f.days,
    order: index + 1,
  }));
  const totalDays = stops.reduce((sum, stop) => sum + stop.days, 0);
  const plan: MultiDestinationPlan = { tripType: "multi_destination", stops, totalDays };
  return isValidMultiDestinationPlan(plan) ? plan : null;
}

/** Expansão dia-a-dia: Dia 1 Atenas, Dia 2 Atenas, Dia 3 Creta… */
export function expandPlanToDays(plan: MultiDestinationPlan): string[] {
  return plan.stops.flatMap((stop) => Array.from({ length: stop.days }, () => stop.destination));
}

/** Âncoras Pro na ordem pedida, com os dias exatos do contrato. */
export function multiDestinationAnchors(
  plan: MultiDestinationPlan,
): Array<{ name: string; days: number }> {
  return plan.stops.map((stop) => ({ name: stop.destination, days: stop.days }));
}

/**
 * Assertion pré-geração: as âncoras têm de espelhar exatamente o contrato
 * (mesmos destinos, mesma ordem, mesmos dias, mesma soma). Devolve a lista de
 * divergências — vazia significa que se pode chamar Places e gerar.
 */
export function contractAnchorMismatches(
  plan: MultiDestinationPlan,
  anchors: Array<{ name: string; days: number }>,
): string[] {
  const problems: string[] = [];
  if (anchors.length !== plan.stops.length) {
    problems.push(`bases: esperado ${plan.stops.length}, obtido ${anchors.length}`);
  }
  plan.stops.forEach((stop, index) => {
    const anchor = anchors[index];
    if (!anchor) {
      problems.push(`base ${index + 1} em falta (${stop.destination})`);
      return;
    }
    if (normalizeDestinationKey(anchor.name) !== stop.key) {
      problems.push(`base ${index + 1}: esperado ${stop.destination}, obtido ${anchor.name}`);
    }
    if (anchor.days !== stop.days) {
      problems.push(`${stop.destination}: esperado ${stop.days} dias, obtido ${anchor.days}`);
    }
  });
  const total = anchors.reduce((sum, anchor) => sum + anchor.days, 0);
  if (total !== plan.totalDays) {
    problems.push(`total: esperado ${plan.totalDays} dias, obtido ${total}`);
  }
  return problems;
}
